/**
 * voiceQueue — the cancel window a spoken command waits in before it sends.
 *
 * **Why this is a module and not four refs in the composer.** Two timings
 * interact here, and they were wrong in a way that only shows up in the way the
 * feature is meant to be used. A finished transcript waits SEND_DELAY_MS (2s)
 * before sending; the recognizer re-arms VOICE_RESTART_MS (400ms) after a
 * result, because Safari ends a session after every utterance. So from 400ms
 * the microphone is live again WHILE the previous command is still waiting —
 * and two short commands spoken back to back ("turn the screen off" … "and show
 * station 5") land well inside 2s of each other.
 *
 * The first version replaced the waiting transcript. The first command was
 * silently dropped: no error, no trace, and hands-free means nobody is looking
 * at the screen — the one place the drop was visible is the place the user
 * isn't watching. "I said two things and one happened" is a miserable bug to
 * receive.
 *
 * So utterances QUEUE. Each gets its own full window, in the order spoken.
 * Nothing a user finished saying disappears.
 *
 * Pure and timer-injectable-by-fake-timers, so the sequence that broke —
 * overlapping utterances — is a test rather than a thing you have to reproduce
 * with a microphone.
 */

/**
 * How many utterances may wait BEHIND the one in its window.
 *
 * Queueing fixed a dropped command, but it inverted the property the window
 * exists for. Under the old replace-semantics a burst of ambient speech
 * produced ONE command — the last thing said. Queued, the same burst produces
 * one per utterance, firing 2s apart, still draining long after the room went
 * quiet. And voice mode is deliberately sticky (remembered per browser), so it
 * is on when nobody means to be commanding: a conversation held near the device
 * becomes a series of assistant prompts.
 *
 * Three plus the one in its window is four commands in flight — comfortably
 * past the design case (two or three spoken deliberately, back to back) and a
 * hard bound on what an open microphone in an empty room can do.
 */
export const MAX_QUEUED = 3;

/**
 * @param delayMs    how long a transcript waits before it sends
 * @param send       called with each transcript, in the order spoken
 * @param onChange   (pending, queuedCount) — for rendering
 * @param onDrop     (text) — an utterance refused because the queue was full
 * @param maxQueued  how many may wait behind the pending one
 */
export function createCancelWindow({ delayMs, send, onChange, onDrop,
                                     maxQueued = MAX_QUEUED } = {}) {
  let pending = null;
  let queue = [];
  let timer = null;

  const notify = () => { if (onChange) onChange(pending, queue.length); };

  const clearTimer = () => {
    if (timer) { clearTimeout(timer); timer = null; }
  };

  /** Put a transcript into its window and start its clock. */
  function arm(text) {
    pending = text;
    clearTimer();
    timer = setTimeout(() => { timer = null; flush(); }, delayMs);
    notify();
  }

  /** A finished utterance. Waits its turn if something is already waiting. */
  function offer(text) {
    if (!text) return;
    if (pending) {
      if (queue.length >= maxQueued) {
        // Full. Drop the NEWEST — this one — rather than making room by
        // evicting the oldest. Three reasons, and the direction is a real
        // decision rather than an obvious one:
        //
        //   1. Overflow is past the design case by definition, so the arriving
        //      utterance is the one most likely to be ambient speech rather
        //      than intent — the burst is WHY it overflowed.
        //   2. Evicting the oldest would let chatter delete a command the user
        //      deliberately spoke and has been waiting on, possibly moments
        //      from firing. That is precisely the silent vanish queueing was
        //      built to prevent; re-introducing it at the other end of the
        //      queue would be a poor trade.
        //   3. A full queue then stays full of the SAME items instead of
        //      churning, so what the user is shown remains true.
        //
        // Dropping is not silent: `onDrop` gives the composer something to say.
        if (onDrop) onDrop(text);
        return;
      }
      queue.push(text);
      notify();
      return;
    }
    arm(text);
  }

  /** Send what's waiting NOW, then start the next one's window. */
  function flush() {
    clearTimer();
    const p = pending;
    pending = null;
    if (p && send) send(p);
    const next = queue.shift();
    if (next) arm(next);    // arm() notifies
    else notify();
  }

  /**
   * Drop everything — the waiting transcript AND the queue. Also the teardown
   * for unmount and for switching the mode off: no timer survives it, so
   * nothing can send afterwards.
   *
   * ✕ means stop, not "skip one": someone cancelling a misheard command does
   * not expect the next one to sail past a second later.
   *
   * There is deliberately NO permanent `dispose()` — a one-way "dead" flag set
   * from an effect cleanup would be tripped by React StrictMode's
   * mount-unmount-mount in development and leave voice silently inert under
   * `npm start`, working only in the production bundle we deploy. Clearing the
   * state is the whole of what teardown needs.
   */
  function cancel() {
    clearTimer();
    pending = null;
    queue = [];
    notify();
  }

  return {
    offer,
    flush,
    cancel,
    get pending() { return pending; },
    get queuedCount() { return queue.length; },
  };
}
